/// <amd-module name="Artifacts/Feed/hooks/PackageList" />
import {
  FilterStates,
  IPackageListFilterBarProps,
} from "Artifacts/Feed/components/PackageListFilterBar";
import { useCrossProtocolCommands } from "Artifacts/Feed/hooks/CrossProtocolCommands";
import { useInfiniteScrolling } from "Artifacts/Feed/hooks/InfiniteScrolling";
import {
  FeedHubActionTypes,
  FetchType,
  IPackageListLoadPayload,
  IProblemPackageListLoadPayload,
} from "Artifacts/Feed/redux";
import { PackageListDataProviderParams } from "Artifacts/Feed/services/PackageListService";
import { CommonConstants, ComponentState } from "Artifacts/Root/Constants";
import { getFeedNameWithNewView, getSelectedView } from "Artifacts/Root/helpers/FeedHelpers";
import { useFeed } from "Artifacts/Root/hooks/FeedUrlResolver";
import { IProtocolService } from "Artifacts/Root/protocols/services/ProtocolService";
import { ICommand } from "Artifacts/Root/protocols/types";
import * as RootRes from "Artifacts/Root/Resources";
import { IFeed, IPackageVersionIdentity } from "Artifacts/Root/types";
import {
  PackageListDataItem,
  UpstreamSourceIdentifier,
  ViewIdentifier,
} from "Artifacts/Root/types/AzureArtifacts";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";
import { ActionWithPayload } from "redux/Utils/ActionHelper";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { IVssPageContext } from "VSS/Platform/Context";
import { useComponentContext, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import * as Storage from "VSS/Platform/Util/Storage";
import { IListSelection, ListSelection } from "VSSUI/List";
import {
  Filter,
  FILTER_CHANGE_EVENT,
  FILTER_RESET_EVENT,
  IFilterItemState,
  IFilterState,
} from "VSSUI/Utilities/Filter";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

const infiniteScrollPageSize = 50;

export interface IUsePackageList {
  feed: IFeed | undefined;
  feedResolution: ComponentState;
  view: ViewIdentifier | undefined;
  filterBarProps: IPackageListFilterBarProps;
  packageListSelection: IListSelection;
  scrollRef: React.MutableRefObject<HTMLDivElement | null>;
  isFilterActive: () => boolean;
  commands: ICommand[];
}

/**
 * usePacakgeList encapsulates package list state management and behavior, including filtering, infinite scrolling,
 * selection.
 * @param loadPackages Redux action dispatcher to fetch package data from server.
 * @param currentRoute URL route for the current experience. Required for a view change.
 */
export function usePackageList(
  loadPackages: (
    feed: IFeed,
    view?: ViewIdentifier | undefined,
    params?: PackageListDataProviderParams | undefined,
    fetchType?: FetchType,
  ) => ActionWithPayload<FeedHubActionTypes, IPackageListLoadPayload>,
  currentRoute: string,
  packageData?: PackageListDataItem[],
): IUsePackageList {
  const { pageContext } = useComponentContext();
  const [currentFeedId, setCurrentFeedId] = useState<string | undefined>();
  const [currentViewId, setCurrentViewId] = useState<string | undefined>();
  const [packageListOffset, setPackageListOffset] = useState<number>(0);
  const [packageListSelection] = useState<IListSelection>(
    () => new ListSelection({ multiSelect: true, selectOnFocus: false }),
  );

  // Allow the server to redirect us to the best feed/view if we don't
  // have permission to access the requested feed/view from the URL
  const { feed, resolution } = useFeed(true);
  const view = getSelectedView(feed);
  const history = useHistory();

  const changeView = useCallback(
    (viewIdentifier) => {
      feed &&
        history.replace(
          generatePath(currentRoute, {
            feedIdentifier: getFeedNameWithNewView(feed.name, viewIdentifier),
          }),
        );
    },
    [currentRoute, feed, history],
  );

  const packageIdentities = useMemo(
    () =>
      packageData?.map(
        (pd) =>
          ({
            name: pd.package.name,
            packageId: pd.package.id,
            version: pd.latestVersion.version,
            normalizedVersion: pd.latestVersion.normalizedVersion,
            protocol: pd.package.protocol,
            views: pd.latestVersion.views,
            isListed: pd.latestVersion.isListed,
            isDeleted: pd.latestVersion.isDeleted,
          } as IPackageVersionIdentity),
      ),
    [packageData],
  );

  // Protocol hooks
  const { commands, clearCommands } = useCrossProtocolCommands(
    packageIdentities,
    packageListSelection,
    feed,
  );

  const { filter, filterBarProps, isFilterActive } = usePackageListFilter(
    feed,
    loadPackages,
    changeView,
    () => setPackageListOffset(0),
    packageListSelection,
    clearCommands,
    view,
  );

  useEffect(() => {
    if (feed && (feed.id !== currentFeedId || view?.id !== currentViewId)) {
      const { nameFilter, sourceFilter } = getFilterStates(filter.getState());
      setPackageListOffset(0);
      loadPackages(feed, view, {
        packageName: nameFilter?.value,
        directUpstreamSourceId: sourceFilter?.value[0],
      });
      saveFeedNameInLocalStorage(pageContext, feed);
      setCurrentFeedId(feed.id);
      setCurrentViewId(view?.id);
    }
  }, [loadPackages, feed, filter, pageContext, view, currentFeedId, currentViewId]);

  const scrollRef = useInfiniteScrolling(() => {
    if (feed) {
      const { nameFilter, sourceFilter } = getFilterStates(filter.getState());
      loadPackages(
        feed,
        view,
        {
          skip: packageListOffset + infiniteScrollPageSize,
          packageName: nameFilter?.value,
          directUpstreamSourceId: sourceFilter?.value[0],
        },
        "amend",
      );
      setPackageListOffset((value) => value + infiniteScrollPageSize);
    }
  });

  return {
    feed,
    feedResolution: resolution,
    view,
    filterBarProps,
    packageListSelection,
    scrollRef,
    isFilterActive,
    commands,
  };
}

interface IPackageListFilterHook {
  filter: Filter;
  filterBarProps: IPackageListFilterBarProps;
  isFilterActive: () => boolean;
}

/**
 * usePackageListFilter provides state management, effects and props generation
 * for a feed hub filter bar.
 * @param feed Current feed
 * @param loadPackages Redux action dispatcher to fetch package data from server.
 * @param changeView Callback that changes currently selected view in root state and in URL
 * @param resetPage Callback that resets pagination values for a package list when any filter applied and new data is about to be loaded.
 * @param view Current view
 */
export function usePackageListFilter(
  feed: IFeed | undefined,
  loadPackages: (
    feed: IFeed,
    view?: ViewIdentifier | undefined,
    params?: PackageListDataProviderParams | undefined,
    fetchType?: FetchType,
  ) => ActionWithPayload<FeedHubActionTypes, IPackageListLoadPayload>,
  changeView: (viewIdOrName?: string) => void,
  resetPage: () => void,
  selection: IListSelection,
  clearCommands: () => void,
  view?: ViewIdentifier,
): IPackageListFilterHook {
  const { pageContext } = useComponentContext();
  const [filter] = useState(() => new Filter());

  const [currentFeedId, setCurrentFeedId] = useState<string | undefined>();

  const onFilterChange = useCallback(
    (stateChange: IFilterState) => {
      // Debounce package loading to have more smooth user experience.
      const debouncedAction = new TimerManagement().debounce(loadPackages, 200) as any;
      const newState = getFilterStates(stateChange);
      const { nameFilter, viewFilter, sourceFilter } = getFilterStates(filter.getState());

      resetPage();
      if (
        feed &&
        newState.viewFilter &&
        newState.viewFilter.value &&
        newState.viewFilter.value[0] !== view?.id
      ) {
        changeView(viewFilter?.value[0]);
        return;
      }

      const hasNameFilterChanged = Object.prototype.hasOwnProperty.call(
        stateChange,
        FilterStates.PackageName,
      );
      const hasSourceFilterChanged = Object.prototype.hasOwnProperty.call(
        stateChange,
        FilterStates.Source,
      );
      if ((hasNameFilterChanged || hasSourceFilterChanged) && feed) {
        debouncedAction(
          feed,
          view,
          { packageName: nameFilter?.value, directUpstreamSourceId: sourceFilter?.value[0] },
          "filtering",
        );
        clearCommands();
        selection.clear();
      }
    },
    [loadPackages, filter, resetPage, feed, view, changeView, clearCommands, selection],
  );

  useEffect(() => {
    if (view) {
      filter.setFilterItemState(FilterStates.View, { value: [view.id] });
    } else {
      filter.setFilterItemState(FilterStates.View, { value: undefined });
    }
    filter.subscribe(onFilterChange, FILTER_CHANGE_EVENT);
    filter.subscribe(onFilterChange, FILTER_RESET_EVENT);
    return () => {
      filter.unsubscribe(onFilterChange, FILTER_CHANGE_EVENT);
      filter.unsubscribe(onFilterChange, FILTER_RESET_EVENT);
    };
  }, [filter, onFilterChange, view]);

  useEffect(() => {
    // Reset filter if we're changing current feed.
    if (feed && feed.id !== currentFeedId) {
      filter.reset();
      setCurrentFeedId(feed.id);
    }
  }, [feed, filter, currentFeedId]);

  const isFilterActive = (): boolean => {
    const { nameFilter, viewFilter, sourceFilter } = getFilterStates(filter.getState());
    return (
      (nameFilter?.value && nameFilter.value[0]) ||
      (sourceFilter?.value && sourceFilter.value[0]) ||
      (viewFilter?.value && viewFilter.value[0])
    );
  };

  const filterBarProps = getFilterBarProps(pageContext, filter, feed);

  return { filter, filterBarProps, isFilterActive };
}

function getFilterStates(
  filterState: IFilterState,
): {
  nameFilter: IFilterItemState | null;
  sourceFilter: IFilterItemState | null;
  viewFilter: IFilterItemState | null;
} {
  const nameFilter = filterState[FilterStates.PackageName];
  const sourceFilter = filterState[FilterStates.Source];
  const viewFilter = filterState[FilterStates.View];
  return { nameFilter, sourceFilter, viewFilter };
}

function getFilterBarProps(pageContext: IVssPageContext, filter: Filter, feed: IFeed | undefined) {
  const service: IProtocolService = pageContext.getService<IProtocolService>("IProtocolService");
  const filterBarProps: IPackageListFilterBarProps = {
    filter,
    viewItems: new ArrayItemProvider(
      feed?.views.map((view) => {
        return { id: view.id, text: view.name, value: view };
      }) || [],
    ),
    sourceItems: new ArrayItemProvider(
      [
        {
          id: "00000000-0000-0000-0000-000000000000",
          text: RootRes.FeedSources_Local,
          value: (null as unknown) as UpstreamSourceIdentifier,
        },
      ].concat(
        feed?.upstreamSources
          .filter((source) => !source.isDeleted)
          .map((source) => {
            const protocol = service.getProtocolById(source.protocol);
            return {
              id: source.id,
              text: source.name,
              value: source,
              iconProps: {
                render: () => (
                  <WrappedComponent
                    wrappedType={protocol.getLogoContributionId()}
                    dependencies={[protocol.getLogoContributionId()]}
                    wrappedProps={{ className: "source-filter-icon", alt: "" }}
                  />
                ),
              },
            };
          }) || [],
      ),
    ),
  };
  return filterBarProps;
}

export function useProblemPackages(
  feed: IFeed | undefined,
  showCFSUI: boolean,
  loadProblemPackages: (
    feed: IFeed,
  ) => ActionWithPayload<FeedHubActionTypes, IProblemPackageListLoadPayload>,
): void {
  const { pageContext } = useComponentContext();

  useEffect(() => {
    feed && showCFSUI && loadProblemPackages(feed);
  }, [pageContext, loadProblemPackages, showCFSUI, feed?.id]);
}

function saveFeedNameInLocalStorage(pageContext: IVssPageContext, feed: IFeed) {
  const pageData = pageContext.getService<IVssPageService>("IVssPageService").getData();
  const lastSelectedFeeds = Storage.tryGetLocalStorageObject<Record<string, string>>(
    CommonConstants.LastOpenedFeedLocalStorageKey,
    {},
  );
  if (pageData && lastSelectedFeeds) {
    lastSelectedFeeds[pageData.hostName] = feed.name;
  }

  Storage.trySetLocalStorageObject(
    CommonConstants.LastOpenedFeedLocalStorageKey,
    lastSelectedFeeds,
  );
}
